import { format } from 'date-fns';
import { ChangeEvent, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { accountabilityAPI } from '../../../services/accountabilityAPI';
import {
  WorkerAccountabilityViolation,
  ViolationFilters,
  Pagination,
} from '../../../types/accountability';

import Alert from '@/components/Alert';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextArea from '@/components/TextArea';
import TextSelect from '@/components/TextSelect';
import TextStack from '@/components/TextStack';
import { Body } from '@/components/Typography';
import Button from '@/components/Button';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import FormColumns from '@/form/FormColumns';

const headerFields = [
  'ID',
  'Worker',
  'Type',
  'Occurred At',
  'Hours Before',
  'Status',
  'Appeal Status',
  'Job ID',
  'Actions',
];

type ActionModalProps = {
  type: 'forgive' | 'remove';
  violationId: number;
  onClose: () => void;
  onSuccess: () => void;
};

const ActionModal = ({
  type,
  violationId,
  onClose,
  onSuccess,
}: ActionModalProps) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) return;

    setLoading(true);
    setError(null);

    try {
      if (type === 'forgive') {
        await accountabilityAPI.forgiveViolation(violationId, reason);
      } else {
        await accountabilityAPI.removeViolation(violationId, reason);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.[0] || `Failed to ${type} violation`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={type === 'forgive' ? 'Forgive Violation' : 'Remove Violation'}
      onRequestClose={onClose}
    >
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon="warning"
            status="danger"
            title="Error"
          />
        )}
        <FormElement label="Reason (required)">
          <TextArea
            placeholder="Enter reason..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel={type === 'forgive' ? 'Forgive' : 'Remove'}
            isLoading={loading}
            label={type === 'forgive' ? 'Forgive' : 'Remove'}
            status={type === 'forgive' ? 'success' : 'danger'}
            onClick={handleSubmit}
            disabled={!reason.trim()}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export const ViolationsPage = () => {
  const navigate = useNavigate();

  const [violations, setViolations] = useState<WorkerAccountabilityViolation[]>(
    [],
  );
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [filters, setFilters] = useState<ViolationFilters>({
    page: 1,
    per_page: 10,
  });

  const [actionModal, setActionModal] = useState<{
    open: boolean;
    type: 'forgive' | 'remove';
    violationId: number;
  } | null>(null);

  const loadViolations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getViolations({
        ...filters,
        page: currentPage,
        per_page: itemsPerPage,
      });

      if (response.success) {
        setViolations(response.data || response.violations || []);
        setPagination(response.pagination || null);
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to load violations');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => {
    loadViolations();
  }, [loadViolations]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleNumberItemsChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setCurrentPage(1);
    setItemsPerPage(parseInt(event.target.value));
  };

  const getViolationTypeBadge = (type: string) => {
    return type === 'late_drop' ? (
      <Badge label="Late Drop" status="warning" />
    ) : (
      <Badge label="No Show" status="danger" />
    );
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { label: string; status: 'success' | 'danger' | 'neutral' }
    > = {
      active: { label: 'Active', status: 'danger' },
      forgiven: { label: 'Forgiven', status: 'success' },
      admin_removed: { label: 'Removed', status: 'neutral' },
    };

    const config = statusMap[status] || { label: status, status: 'neutral' };
    return <Badge label={config.label} status={config.status} />;
  };

  const getAppealStatusBadge = (status: string) => {
    if (status === 'none') return <Body color="light">-</Body>;

    const statusMap: Record<
      string,
      { label: string; status: 'warning' | 'success' | 'danger' | 'theme' }
    > = {
      pending: { label: 'Pending', status: 'warning' },
      approved: { label: 'Approved', status: 'success' },
      denied: { label: 'Denied', status: 'danger' },
    };

    const config = statusMap[status] || { label: status, status: 'theme' };
    return <Badge label={config.label} status={config.status} />;
  };

  const openActionModal = (type: 'forgive' | 'remove', violationId: number) => {
    setActionModal({ open: true, type, violationId });
  };

  const closeActionModal = () => {
    setActionModal(null);
  };

  const handleActionSuccess = () => {
    setSuccess(
      actionModal?.type === 'forgive'
        ? 'Violation forgiven successfully'
        : 'Violation removed successfully',
    );
    loadViolations();
  };

  const totalItems = pagination?.total_count || 0;

  return (
    <Page
      footer={
        violations.length > 0 && (
          <Paginator
            currentPage={currentPage}
            handleNumberItemsChange={handleNumberItemsChange}
            handlePageChange={handlePageChange}
            itemsLength={totalItems}
            itemsPerPage={itemsPerPage}
          />
        )
      }
      size="full"
      title="Violations"
    >
      {actionModal && (
        <ActionModal
          type={actionModal.type}
          violationId={actionModal.violationId}
          onClose={closeActionModal}
          onSuccess={handleActionSuccess}
        />
      )}

      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            {error && (
              <Alert
                description={error}
                icon="warning"
                status="danger"
                title="Error"
                onDismiss={() => setError(null)}
              />
            )}
            {success && (
              <Alert
                description={success}
                icon="check"
                status="success"
                title="Success"
                onDismiss={() => setSuccess(null)}
              />
            )}
            <FormColumns>
              <FormElement label="Type">
                <TextSelect
                  value={filters.violation_type || ''}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setFilters({
                      ...filters,
                      violation_type: (e.target.value as any) || undefined,
                    })
                  }
                >
                  <option value="">All Types</option>
                  <option value="late_drop">Late Drop</option>
                  <option value="no_show">No Show</option>
                </TextSelect>
              </FormElement>
              <FormElement label="Status">
                <TextSelect
                  value={filters.status || ''}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setFilters({
                      ...filters,
                      status: (e.target.value as any) || undefined,
                    })
                  }
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="forgiven">Forgiven</option>
                  <option value="admin_removed">Removed</option>
                </TextSelect>
              </FormElement>
              <FormElement label="Appeal Status">
                <TextSelect
                  value={filters.appeal_status || ''}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setFilters({
                      ...filters,
                      appeal_status: (e.target.value as any) || undefined,
                    })
                  }
                >
                  <option value="">All Appeal Statuses</option>
                  <option value="none">None</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                </TextSelect>
              </FormElement>
            </FormColumns>
          </Stack>
        </Card.Section>

        <Table>
          <TableHeader fields={headerFields} />
          <tbody>
            {violations.map((violation) => (
              <TableRow key={violation.id}>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body color="theme" weight="medium">
                      #{violation.id}
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  <Stack gap={20}>
                    <TextStack spacing="tight">
                      <Link to={`/workers/${violation.worker_id}`}>
                        <Body color="theme" weight="medium">
                          {violation.worker_name}
                        </Body>
                      </Link>
                    </TextStack>
                  </Stack>
                </TableCell>
                <TableCell>
                  {getViolationTypeBadge(violation.violation_type)}
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {format(
                        new Date(violation.occurred_at),
                        'MMM dd, yyyy HH:mm',
                      )}
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {Number(violation.hours_before_shift).toFixed(1)}h
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell>{getStatusBadge(violation.status)}</TableCell>
                <TableCell>
                  {getAppealStatusBadge(violation.appeal_status)}
                </TableCell>
                <TableCell>
                  <Stack gap={20}>
                    <TextStack spacing="tight">
                      {violation.order_id ? (
                        <Link
                          to={`/orders/${violation.order_id}/jobs/${violation.job_id}`}
                        >
                          <Body color="theme" weight="medium">
                            #{violation.job_id}
                          </Body>
                        </Link>
                      ) : (
                        <Body weight="medium">#{violation.job_id}</Body>
                      )}
                    </TextStack>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack gap={8}>
                    {violation.status === 'active' && (
                      <>
                        <Button
                          a11yLabel="Forgive violation"
                          appearance="outline"
                          size="sm"
                          status="success"
                          label="Forgive"
                          onClick={() =>
                            openActionModal('forgive', violation.id)
                          }
                        />
                        <Button
                          a11yLabel="Remove violation"
                          appearance="outline"
                          size="sm"
                          status="danger"
                          label="Remove"
                          onClick={() =>
                            openActionModal('remove', violation.id)
                          }
                        />
                      </>
                    )}
                    {violation.appeal_status === 'pending' && (
                      <Button
                        a11yLabel="Review appeal"
                        appearance="outline"
                        size="sm"
                        label="Review"
                        onClick={() =>
                          navigate(
                            `/accountability/appeals?violation_id=${violation.id}`,
                          )
                        }
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <LoadingState overlayColor="white" />
                </TableCell>
              </TableRow>
            )}
            {!loading && violations.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <Body>No violations found. Try adjusting your filters.</Body>
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </Card>
    </Page>
  );
};

export default ViolationsPage;
